import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  useAlert,
} from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CITY_PAGE_SIZE,
  CityFormModal,
  CitySearchBar,
  CityTable,
  DeleteCityModal,
  useCityFilters,
  useCityList,
  useDeleteCity,
} from '@/features/location';
import type { CityViewModel } from '@/shared/api/generated';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

export const Route = createFileRoute('/_authenticated/location/cities/')({
  component: CityListPage,
});

/** Página de listagem de Cidades (área de Localização). */
export function CityListPage() {
  const { t } = useTranslation('location');
  const { alert } = useAlert();

  const { cities, isLoading, isError, refetch } = useCityList();
  const { filters, updateFilters } = useCityFilters();
  const deleteCity = useDeleteCity();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CityViewModel | null>(null);
  const [deleting, setDeleting] = useState<CityViewModel | null>(null);

  // TODO: busca client-side — o endpoint `GET /api/v1/location/cities` ainda não
  // aceita parâmetro de busca. Quando o backend implementar (ex.: `?search=`),
  // enviar `filters.search` via `useCityList` e remover a filtragem abaixo.
  const searched = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    if (!query) return cities;
    return cities.filter(
      city =>
        (city.name ?? '').toLowerCase().includes(query) ||
        (city.code ?? '').toLowerCase().includes(query),
    );
  }, [cities, filters.search]);

  // TODO: paginação client-side — o endpoint retorna a lista completa. Quando o
  // backend paginar (page/pageSize/total), passar esses parâmetros para
  // `useCityList`, usar os totais do envelope e remover o fatiamento. Ver `CITY_PAGE_SIZE`.
  const totalPages = Math.max(1, Math.ceil(searched.length / CITY_PAGE_SIZE));
  const currentPage = Math.min(filters.page, totalPages);
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * CITY_PAGE_SIZE;
    return searched.slice(start, start + CITY_PAGE_SIZE);
  }, [searched, currentPage]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (city: CityViewModel) => {
    setEditing(city);
    setFormOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleting?.id == null) return;
    deleteCity.mutate(
      { id: deleting.id },
      {
        onSuccess: () => alert({ alertType: 'success', title: t('cities.toast.deleteSuccess') }),
        onError: () => alert({ alertType: 'warning', title: t('cities.toast.deleteError') }),
        onSettled: () => setDeleting(null),
      },
    );
  };

  const addButton = (
    <Button hierarchy="primary" size="lg" leadingIcon={PlusIcon} onClick={openCreate}>
      {t('cities.actions.add')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-xxs">
      <PageHeader
        title={t('cities.title')}
        subtitle={t('cities.subtitle', { count: cities.length })}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to="/location/countries">{t('cities.breadcrumb.location')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('cities.breadcrumb.cities')}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={addButton}
      />

      {isError ? (
        <div className="flex flex-col items-start gap-nano rounded-md border-hairline border-base-danger-300 bg-ctx-layout-surface p-xxs">
          <p className="text-base-danger-700 ts-paragraph-xs">{t('cities.toast.loadError')}</p>
          <Button hierarchy="secondary" size="md" onClick={() => void refetch()}>
            {t('cities.loadErrorRetry')}
          </Button>
        </div>
      ) : (
        <>
          <CitySearchBar
            defaultValue={filters.search}
            onSearch={search => updateFilters({ search, page: 1 })}
            onClear={() => updateFilters({ search: '', page: 1 })}
          />

          <CityTable
            data={pageData}
            isLoading={isLoading}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={searched.length}
            pageSize={CITY_PAGE_SIZE}
            onPageChange={page => updateFilters({ page })}
            emptyStateAction={addButton}
            onEdit={openEdit}
            onDelete={setDeleting}
          />
        </>
      )}

      <CityFormModal
        open={formOpen}
        city={editing}
        onOpenChange={open => {
          setFormOpen(open);
          if (!open) setEditing(null);
        }}
      />

      <DeleteCityModal
        open={deleting !== null}
        cityName={deleting?.name ?? ''}
        onConfirm={handleDeleteConfirm}
        onOpenChange={open => {
          if (!open) setDeleting(null);
        }}
      />
    </div>
  );
}
